import {
  bulkState,
  buildCatalog,
  type BulkState,
  type PricingData,
  type PrintSelection,
} from '@rpl/pricing';
import { useMemo } from 'react';
import { formatMoney } from '../../lib/format';
import { useStore } from '../../store/store';

/** Minimum would-be saving before we bother the customer with a nudge. */
const MIN_NUDGE_SAVING = 1;

/**
 * Bulk-pricing nudge bars. Active sizes flip to a green-tinted "applied — you
 * saved $X" confirmation; among inactive C-Type sizes we show only the single
 * best candidate (largest saving at threshold, ≥ $1) to avoid nudge spam.
 */
export function BulkNudge({
  pricing,
  selections,
}: {
  pricing: PricingData;
  selections: PrintSelection[];
}) {
  const setStep = useStore((s) => s.setStep);
  const { active, candidate, sizeName } = useMemo(() => {
    const states = bulkState(pricing, selections);
    const catalog = buildCatalog(pricing);
    const sizeName = (s: BulkState) => catalog.get(s.sizeId)?.size ?? s.sizeId;
    const active = states.filter((s) => s.active);
    const candidate = states
      .filter((s) => !s.active && s.savingAtThreshold >= MIN_NUDGE_SAVING)
      .sort((a, b) => b.savingAtThreshold - a.savingAtThreshold)[0];
    return { active, candidate, sizeName };
  }, [pricing, selections]);

  if (active.length === 0 && !candidate) return null;

  return (
    <>
      {active.map((s) => (
        <div key={s.sizeId} className="nudge nudge--active" role="status">
          <span className="nudge__icon">✓</span>
          <div className="nudge__copy">
            <strong>Bulk pricing applied.</strong> Every {sizeName(s)}&quot; is now{' '}
            {formatMoney(s.bulkPrice)}. You saved <strong>{formatMoney(s.savingAtThreshold)}</strong>{' '}
            on this order.
          </div>
        </div>
      ))}
      {candidate && (
        <div className="nudge">
          <span className="nudge__icon">◐</span>
          <div className="nudge__copy">
            <strong>
              {candidate.remaining} more {sizeName(candidate)}&quot; prints
            </strong>{' '}
            unlocks bulk pricing. Every {sizeName(candidate)}&quot; drops to{' '}
            {formatMoney(candidate.bulkPrice)} and you&rsquo;d save{' '}
            <strong>{formatMoney(candidate.savingAtThreshold)}</strong> on this order.
          </div>
          <button type="button" className="nudge__link" onClick={() => setStep(0)}>
            Add more photos
          </button>
        </div>
      )}
    </>
  );
}
